import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiImage, FiTrash2, FiFilm, FiZap, FiCheck, FiLoader, FiAlertTriangle, FiSquare, FiFileText } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { formatSeconds, showError } from '../FullStory/shared';

const BLOCK_STYLE = {
  anchor: 'bg-purple-50 text-purple-700 border-purple-200',
  voice: 'bg-blue-50 text-blue-700 border-blue-200',
  spot: 'bg-amber-50 text-amber-800 border-amber-200',
  byte: 'bg-orange-50 text-orange-800 border-orange-200',
  gfx: 'bg-red-50 text-red-700 border-red-200',
};

const formatBytes = (n) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : `${Math.max(1, Math.round(n / 1024 ** 2))} MB`);

// Spelled out, not just "video/*": Windows only offers files whose type it
// knows, which hides .mxf, .mkv, .mts and others — and on some PCs .avi/.mov.
const VIDEO_EXTENSIONS = ['.mp4', '.m4v', '.mov', '.qt', '.avi', '.mkv', '.webm', '.wmv', '.asf', '.flv', '.f4v', '.mpg', '.mpeg', '.m2v',
  '.vob', '.ts', '.mts', '.m2ts', '.3gp', '.3g2', '.mxf', '.dv', '.ogv', '.divx', '.rm', '.rmvb', '.hevc', '.h265', '.h264', '.264'];
const VIDEO_ACCEPT = ['video/*', ...VIDEO_EXTENSIONS].join(',');
const isVideoFile = (file) => file.type.startsWith('video/') || VIDEO_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

/** Step 1: the footage and photos, the notes, and the package script (written by AI or pasted as written). */
export const MaterialStep = ({ story, run, readOnly, setStory, uploading, setUploading }) => {
  const t = useT();
  const id = story._id;
  const [notes, setNotes] = useState(story.script.input || '');
  const [text, setText] = useState(story.script.text || '');
  const [dirty, setDirty] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [minutes, setMinutes] = useState(String(Math.round((story.targetSeconds || 180) / 60)));
  const videoInput = useRef(null);
  const photoInput = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { if (!dirty && !streaming) setText(story.script.text || ''); }, [story.script.text, dirty, streaming]);

  // Live preview of how the script reads as a package.
  useEffect(() => {
    if (!text.trim()) { setPreview(null); return undefined; }
    const timer = setTimeout(() => { api.parse(text).then(setPreview).catch(() => {}); }, 500);
    return () => clearTimeout(timer);
  }, [text]);

  const uploadVideos = async (files) => {
    for (const file of files) {
      if (!isVideoFile(file)) { toast.error(t('fullStoryVideo.notVideo', { name: file.name })); continue; }
      if (file.size > story.limits.MAX_SOURCE_BYTES) { toast.error(t('fullStoryVideo.tooBig', { name: file.name })); continue; }
      setUploading((u) => ({ ...u, [file.name]: 0 }));
      try {
        const next = await api.uploadSource(id, file, { onProgress: (f) => setUploading((u) => ({ ...u, [file.name]: f })) });
        setStory(next);
      } catch (e) {
        showError(e, t('fullStoryVideo.uploadFailed'));
      } finally {
        setUploading((u) => { const { [file.name]: _, ...rest } = u; return rest; });
      }
    }
  };

  const write = async () => {
    if (dirty && !window.confirm(t('fullStory.discardEdits'))) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true);
    setDirty(false);
    let draft = '';
    setText('');
    await api.streamScript(id, { input: notes }, {
      signal: controller.signal,
      onChunk: ({ text: piece }) => { draft += piece; setText(draft); },
      onDone: ({ story: next }) => setStory(next),
      onError: (e) => showError(e, t('fullStory.scriptFailed')),
    });
    abortRef.current = null;
    setStreaming(false);
    api.get(id).then(setStory).catch(() => {});
  };

  const saveAll = () => run(async () => {
    const next = await api.saveScript(id, { text, input: notes });
    setDirty(false);
    return next;
  });

  const approve = async () => {
    const saved = await run(() => api.saveScript(id, { text, input: notes }));
    if (!saved) return;
    setDirty(false);
    run(() => api.approveScript(id));
  };

  const setTarget = () => {
    const n = Math.round(Number(minutes) * 60);
    if (!Number.isFinite(n) || n < 30 || n > 1800) { toast.error(t('fullStoryVideo.minutesRange')); return; }
    if (n !== story.targetSeconds) run(() => api.update(id, { targetSeconds: n }));
  };

  const sources = story.sources || [];
  const pendingUploads = Object.entries(uploading || {});
  const footageWorking = sources.some((s) => ['queued', 'processing'].includes(s.status)) || pendingUploads.length > 0;
  const canWrite = !readOnly && !streaming && (notes.trim() || sources.some((s) => s.status === 'ready'));

  return (
    <div className="space-y-4">
      {/* Footage */}
      <section className="card space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('fullStoryVideo.footage.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('fullStoryVideo.footage.hint')}</p>
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <input ref={videoInput} type="file" accept={VIDEO_ACCEPT} multiple className="hidden" onChange={(e) => { uploadVideos([...e.target.files]); e.target.value = ''; }} />
              <input ref={photoInput} type="file" accept="image/*" multiple className="hidden"
                onChange={async (e) => { const files = [...e.target.files]; e.target.value = ''; for (const f of files) await run(() => api.addSourceImage(id, f)); }} />
              <button className="btn btn-primary text-sm flex items-center gap-1.5" onClick={() => videoInput.current?.click()} disabled={sources.length >= story.limits.MAX_SOURCES_PER_STORY}>
                <FiUploadCloud size={14} /> {t('fullStoryVideo.footage.uploadVideos')}
              </button>
              <button className="btn btn-ghost text-sm flex items-center gap-1.5" onClick={() => photoInput.current?.click()} disabled={sources.length >= story.limits.MAX_SOURCES_PER_STORY}>
                <FiImage size={14} /> {t('fullStoryVideo.footage.addPhotos')}
              </button>
            </div>
          )}
        </div>

        {pendingUploads.map(([name, fraction]) => (
          <div key={name} className="rounded-lg border border-primary-200 bg-primary-50/50 px-3 py-2">
            <div className="flex justify-between text-xs text-gray-700"><span className="truncate">{name}</span><span className="tabular-nums">{Math.round(fraction * 100)}%</span></div>
            <div className="mt-1.5 h-1.5 rounded bg-white overflow-hidden"><div className="h-full bg-primary-500 transition-all" style={{ width: `${Math.round(fraction * 100)}%` }} /></div>
            <p className="text-[11px] text-gray-500 mt-1">{t('fullStoryVideo.footage.uploadingHint')}</p>
          </div>
        ))}

        {sources.length === 0 && !pendingUploads.length ? (
          <p className="text-sm text-gray-500 py-4 text-center">{t('fullStoryVideo.footage.none')}</p>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-2">
            {sources.map((s) => (
              <li key={s._id} className="flex gap-3 rounded-lg border border-gray-200 p-2">
                <div className="relative w-28 aspect-video rounded bg-gray-100 overflow-hidden flex-shrink-0">
                  {s.poster ? <img src={api.fileUrl(id, s.poster)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <FiFilm className="m-auto mt-4 text-gray-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate" title={s.originalName}>{s.originalName}</p>
                  <p className="text-xs text-gray-500">
                    {s.kind === 'image' ? t('fullStoryVideo.footage.photo') : formatSeconds(s.durationSec)}
                    {s.size ? ` · ${formatBytes(s.size)}` : ''}
                    {s.status === 'ready' && s.kind === 'video' ? ` · ${t('fullStoryVideo.footage.read', { moments: s.shotCount, lines: s.transcriptLines })}` : ''}
                  </p>
                  {(s.status === 'queued' || s.status === 'processing') && (
                    <>
                      <p className="text-xs text-primary-700 flex items-center gap-1 mt-0.5"><FiLoader size={11} className="animate-spin" /> {t(`fullStoryVideo.stage.${s.stage || 'waiting'}`)} · {s.progress || 0}%</p>
                      <div className="mt-1 h-1 rounded bg-gray-100 overflow-hidden"><div className="h-full bg-primary-400" style={{ width: `${s.progress || 0}%` }} /></div>
                    </>
                  )}
                  {s.status === 'uploading' && <p className="text-xs text-amber-700 mt-0.5">{t('fullStoryVideo.footage.incomplete')}</p>}
                  {s.status === 'failed' && <p className="text-xs text-danger-600 mt-0.5 flex items-center gap-1"><FiAlertTriangle size={11} /> {s.error}</p>}
                  {s.status === 'ready' && <p className="text-xs text-success-700 text-green-700 mt-0.5 flex items-center gap-1"><FiCheck size={11} /> {t('fullStoryVideo.footage.ready')}</p>}
                </div>
                {!readOnly && (
                  <button title={t('fullStory.delete')} className="self-start p-1.5 rounded text-gray-400 hover:text-danger-600 hover:bg-danger-50"
                    onClick={() => { if (window.confirm(t('fullStoryVideo.footage.removeConfirm', { name: s.originalName }))) run(() => api.removeSource(id, s._id)); }}>
                    <FiTrash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Notes and settings */}
      <section className="card space-y-3">
        <h2 className="text-base font-semibold text-gray-900">{t('fullStoryVideo.notes.title')}</h2>
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
          onBlur={() => { if (!readOnly && notes !== story.script.input) saveAll(); }}
          readOnly={readOnly}
          rows={4}
          placeholder={t('fullStoryVideo.notes.placeholder')}
          className="input w-full text-sm"
        />
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2 text-gray-600">
            {t('fullStoryVideo.length')}
            <input type="number" min={1} max={30} value={minutes} readOnly={readOnly}
              onChange={(e) => setMinutes(e.target.value)} onBlur={setTarget} className="input w-20 text-sm py-1" />
            {t('fullStoryVideo.minutes')}
          </label>
          <div className="flex p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
            {['16:9', '9:16'].map((r) => (
              <button key={r} disabled={readOnly} onClick={() => r !== story.aspectRatio && run(() => api.update(id, { aspectRatio: r }))}
                className={`px-2.5 py-1 rounded-md ${story.aspectRatio === r ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>{r}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Script */}
      <section className="card space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('fullStoryVideo.script.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('fullStoryVideo.script.hint')}</p>
          </div>
          {!readOnly && (streaming ? (
            <button className="btn btn-ghost text-sm flex items-center gap-1.5" onClick={() => abortRef.current?.abort()}><FiSquare size={12} /> {t('fullStory.stop')}</button>
          ) : (
            <button className="btn btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50" onClick={write} disabled={!canWrite || footageWorking}
              title={footageWorking ? t('fullStoryVideo.script.waitFootage') : ''}>
              <FiZap size={14} /> {story.script.text ? t('fullStoryVideo.script.rewrite') : t('fullStoryVideo.script.write')}
            </button>
          ))}
        </div>
        {footageWorking && <p className="text-xs text-amber-700">{t('fullStoryVideo.script.waitFootage')}</p>}
        <div className="grid lg:grid-cols-5 gap-3">
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setDirty(true); }}
            readOnly={readOnly || streaming}
            rows={22}
            placeholder={t('fullStoryVideo.script.placeholder')}
            className="input w-full text-sm font-mono leading-relaxed lg:col-span-3"
          />
          <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm space-y-2 max-h-[560px] overflow-y-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><FiFileText size={12} /> {t('fullStoryVideo.preview.title')}</p>
            {!preview ? <p className="text-xs text-gray-400">{t('fullStoryVideo.preview.empty')}</p> : (
              <>
                {preview.package.header && <p><span className="text-xs text-gray-500">{t('fullStoryVideo.preview.header')}:</span> <strong>{preview.package.header}</strong> {preview.package.subheader && <span className="text-gray-600">/ {preview.package.subheader}</span>}</p>}
                {preview.package.location && <p><span className="text-xs text-gray-500">{t('fullStoryVideo.preview.location')}:</span> {preview.package.location}</p>}
                {preview.package.tops.length > 0 && <p className="text-xs text-gray-600">{t('fullStoryVideo.preview.tops', { count: preview.package.tops.length })}</p>}
                <p className="text-xs text-gray-600">{t('fullStoryVideo.preview.sentences', { count: preview.sentences.length })}</p>
                <ol className="space-y-1">
                  {preview.blocks.map((b, i) => (
                    <li key={i} className={`rounded-md border px-2 py-1 text-xs ${BLOCK_STYLE[b.type]}`}>
                      <strong className="uppercase">{t(`fullStoryVideo.block.${b.type}`)}</strong>
                      {b.note ? ` — ${b.note}` : ''}
                      {b.lines?.length ? <span className="block text-gray-700 truncate">{b.lines[0]}{b.lines.length > 1 ? ` (+${b.lines.length - 1})` : ''}</span> : null}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
        {story.script.error && <p className="text-sm text-danger-600">{story.script.error}</p>}
        {!readOnly && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-500">{story.script.approved && !dirty ? t('fullStoryVideo.script.approved') : t('fullStoryVideo.script.approveHint')}</p>
            <div className="flex gap-2">
              {dirty && <button className="btn btn-ghost text-sm" onClick={saveAll}>{t('fullStory.saveEdits')}</button>}
              <button className="btn btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50" disabled={!text.trim() || streaming} onClick={approve}>
                <FiCheck size={14} /> {t('fullStoryVideo.script.approve')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default MaterialStep;
